import axios from 'axios';
import { API_URL } from '../config';
import { getErrorMessage } from '../utils/errorUtils';

// Function to get CSRF token from cookies
const getCSRFToken = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }

  return cookieValue;
};

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include credentials for session authentication
});

// Initialize CSRF token by fetching it from the backend
let csrfInitialized = false;
let csrfInitPromise = null;
let csrfInitAttempts = 0;
const MAX_CSRF_INIT_ATTEMPTS = 3;

const initializeCSRF = async () => {
  if (csrfInitialized) return Promise.resolve();

  // If initialization is in progress, return the existing promise
  if (csrfInitPromise) return csrfInitPromise;

  csrfInitAttempts++;

  csrfInitPromise = axios.get(`${API_URL}/api/v1/common/csrf-token/`, {
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then((response) => {
      csrfInitialized = true;

      // Verify token is actually in cookies
      const token = getCSRFToken();
      if (!token) {
        throw new Error('CSRF token was not set in cookies after initialization');
      }

      return response;
    })
    .catch((error) => {
      csrfInitPromise = null; // Reset so it can be retried

      // Retry with exponential backoff if we haven't exceeded max attempts
      if (csrfInitAttempts < MAX_CSRF_INIT_ATTEMPTS) {
        const delay = Math.pow(2, csrfInitAttempts) * 1000; // 2s, 4s, 8s
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(initializeCSRF());
          }, delay);
        });
      }

      throw error;
    });

  return csrfInitPromise;
};

// Initialize CSRF token when the module loads
initializeCSRF();

// Add request interceptor
api.interceptors.request.use(
  async (config) => {
    // Ensure CSRF is initialized before making state-changing requests
    const method = config.method?.toLowerCase();
    if (method && ['post', 'put', 'patch', 'delete'].includes(method)) {
      // Wait for CSRF initialization if it's in progress
      try {
        await initializeCSRF();
      } catch (error) {
        // CSRF init failed, request will proceed without token
      }

      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        // Try to re-initialize CSRF token one more time
        try {
          csrfInitialized = false;
          csrfInitPromise = null;
          await initializeCSRF();
          const retryToken = getCSRFToken();
          if (retryToken) {
            config.headers['X-CSRFToken'] = retryToken;
          }
        } catch (retryError) {
          // CSRF retry failed, request will proceed without token
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Enhance error with user-friendly message
    if (error.response || error.request) {
      // Add a user-friendly message to the error object
      error.userMessage = getErrorMessage(error);
    }
    // Let the calling code handle authentication errors
    return Promise.reject(error);
  }
);

export const resetCSRF = () => {
  csrfInitialized = false;
  csrfInitPromise = null;
  csrfInitAttempts = 0;
};

export default api;
